import React, { useState, useEffect, useRef } from 'react';
import { X, Moon, Sun, Maximize2, Minimize2, BookOpen, ChevronLeft, ChevronRight, Type, Download } from 'lucide-react';
import DOMPurify from 'isomorphic-dompurify';

/**
 * ReadingModeModal - Distraction-free article reader
 * Beat MyMind with clean typography, dark mode, and reading progress
 */
const ReadingModeModal = ({ isOpen, onClose, article }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fontSize, setFontSize] = useState(18); // 16, 18, 20, 22, 24
  const [readingProgress, setReadingProgress] = useState(0);
  const [readingTime, setReadingTime] = useState(0);
  const contentRef = useRef(null);

  // Calculate reading time (average 200 words per minute)
  useEffect(() => {
    if (article?.content || article?.raw_text) {
      const text = article.content || article.raw_text || '';
      const wordCount = text.split(/\s+/).length;
      const minutes = Math.ceil(wordCount / 200);
      setReadingTime(minutes);
    }
  }, [article]);

  // Track scroll progress
  useEffect(() => {
    if (!isOpen || !contentRef.current) return;

    const handleScroll = () => {
      const element = contentRef.current;
      if (!element) return;

      const scrollTop = element.scrollTop;
      const scrollHeight = element.scrollHeight - element.clientHeight;
      const progress = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      
      setReadingProgress(Math.min(Math.round(progress), 100));
    };

    const element = contentRef.current;
    element.addEventListener('scroll', handleScroll);
    return () => element.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  // Handle keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e) => {
      // ESC to close
      if (e.key === 'Escape' && !isFullscreen) {
        onClose();
      }
      // F for fullscreen
      if (e.key === 'f' || e.key === 'F') {
        setIsFullscreen(prev => !prev);
      }
      // D for dark mode
      if (e.key === 'd' || e.key === 'D') {
        setIsDarkMode(prev => !prev);
      }
      // + to increase font
      if (e.key === '+' || e.key === '=') {
        setFontSize(prev => Math.min(prev + 2, 24));
      }
      // - to decrease font
      if (e.key === '-' || e.key === '_') {
        setFontSize(prev => Math.max(prev - 2, 16));
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [isOpen, isFullscreen, onClose]);

  if (!isOpen || !article) return null;

  // Get article content
  const getArticleContent = () => {
    // Priority: archived_html > meta.article_html > raw_text
    if (article.meta?.archived_html) {
      return article.meta.archived_html;
    }
    if (article.meta?.article_html) {
      return article.meta.article_html;
    }
    if (article.content) {
      return `<div>${article.content.replace(/\n/g, '<br>')}</div>`;
    }
    if (article.raw_text) {
      return `<div>${article.raw_text.replace(/\n/g, '<br>')}</div>`;
    }
    return '<p>No content available for reading mode.</p>';
  };

  const articleContent = getArticleContent();
  const sanitizedContent = DOMPurify.sanitize(articleContent, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'ul', 'ol', 'li', 'a', 'img', 'code', 'pre'],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class'],
  });

  const handleDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([sanitizedContent], { type: 'text/html' });
    element.href = URL.createObjectURL(file);
    element.download = `${article.title || 'article'}.html`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <div 
      className={`fixed inset-0 z-[70] flex items-center justify-center ${
        isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
      } ${isFullscreen ? '' : 'p-4'}`}
      style={{ transition: 'background-color 0.3s ease' }}
    >
      {/* Progress Bar */}
      <div 
        className={`absolute top-0 left-0 h-1 transition-all duration-300 ${
          isDarkMode ? 'bg-purple-500' : 'bg-purple-600'
        }`}
        style={{ width: `${readingProgress}%` }}
      />

      {/* Header Controls */}
      <div 
        className={`absolute top-0 left-0 right-0 flex items-center justify-between p-4 ${
          isDarkMode ? 'bg-[#1a1a1a]/80' : 'bg-white/80'
        } backdrop-blur-sm z-10`}
        style={{ transition: 'background-color 0.3s ease' }}
      >
        {/* Left Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-white/10 text-white' 
                : 'hover:bg-gray-100 text-gray-700'
            }`}
            title="Close (ESC)"
          >
            <X size={20} />
          </button>
          
          <div className={`flex items-center gap-2 ml-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            <BookOpen size={16} />
            <span className="text-sm">{readingTime} min read</span>
            <span className="text-xs ml-2">{readingProgress}% complete</span>
          </div>
        </div>

        {/* Right Controls */}
        <div className="flex items-center gap-2">
          {/* Font Size Controls */}
          <div className="flex items-center gap-1 mr-2">
            <button
              onClick={() => setFontSize(prev => Math.max(prev - 2, 16))}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-white/10 text-white' 
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              title="Decrease font size (-)"
            >
              <Type size={16} />
            </button>
            <span className={`text-sm px-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {fontSize}px
            </span>
            <button
              onClick={() => setFontSize(prev => Math.min(prev + 2, 24))}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode 
                  ? 'hover:bg-white/10 text-white' 
                  : 'hover:bg-gray-100 text-gray-700'
              }`}
              title="Increase font size (+)"
            >
              <Type size={20} />
            </button>
          </div>

          {/* Download */}
          <button
            onClick={handleDownload}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-white/10 text-white' 
                : 'hover:bg-gray-100 text-gray-700'
            }`}
            title="Download article"
          >
            <Download size={20} />
          </button>

          {/* Dark Mode Toggle */}
          <button
            onClick={() => setIsDarkMode(prev => !prev)}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-white/10 text-white' 
                : 'hover:bg-gray-100 text-gray-700'
            }`}
            title="Toggle dark mode (D)"
          >
            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          {/* Fullscreen Toggle */}
          <button
            onClick={() => setIsFullscreen(prev => !prev)}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode 
                ? 'hover:bg-white/10 text-white' 
                : 'hover:bg-gray-100 text-gray-700'
            }`}
            title="Toggle fullscreen (F)"
          >
            {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          </button>
        </div>
      </div>

      {/* Article Content */}
      <div
        ref={contentRef}
        className={`w-full h-full overflow-y-auto pt-20 pb-12 px-4 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}
        style={{ 
          scrollBehavior: 'smooth',
          transition: 'color 0.3s ease'
        }}
      >
        <article 
          className="mx-auto prose prose-lg max-w-3xl"
          style={{
            fontSize: `${fontSize}px`,
            lineHeight: '1.8',
            transition: 'font-size 0.2s ease'
          }}
        >
          {/* Article Title */}
          {article.title && (
            <h1 
              className={`text-4xl font-bold mb-6 leading-tight ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}
              style={{ transition: 'color 0.3s ease' }}
            >
              {article.title}
            </h1>
          )}

          {/* Article Meta */}
          {(article.meta?.author || article.meta?.published_date) && (
            <div 
              className={`flex items-center gap-4 mb-8 pb-6 border-b ${
                isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'
              }`}
              style={{ transition: 'all 0.3s ease' }}
            >
              {article.meta?.author && (
                <span className="text-sm">By {article.meta.author}</span>
              )}
              {article.meta?.published_date && (
                <span className="text-sm">{new Date(article.meta.published_date).toLocaleDateString()}</span>
              )}
            </div>
          )}

          {/* Article Body */}
          <div
            className={`reader-content ${
              isDarkMode ? 'prose-invert' : ''
            }`}
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            style={{
              // Custom prose styles for better readability
              '--tw-prose-body': isDarkMode ? '#d1d5db' : '#374151',
              '--tw-prose-headings': isDarkMode ? '#f9fafb' : '#111827',
              '--tw-prose-links': isDarkMode ? '#a78bfa' : '#7c3aed',
              '--tw-prose-bold': isDarkMode ? '#f9fafb' : '#111827',
              '--tw-prose-quotes': isDarkMode ? '#9ca3af' : '#6b7280',
              '--tw-prose-code': isDarkMode ? '#fbbf24' : '#d97706',
            }}
          />
        </article>
      </div>

      {/* Keyboard Shortcuts Hint (Bottom Right) */}
      <div 
        className={`absolute bottom-4 right-4 text-xs ${
          isDarkMode ? 'text-gray-500' : 'text-gray-400'
        }`}
        style={{ transition: 'color 0.3s ease' }}
      >
        <div>ESC: Close • F: Fullscreen • D: Dark Mode</div>
        <div>+/-: Font Size</div>
      </div>
    </div>
  );
};

export default ReadingModeModal;



